"use client";
import { Errback } from "@tanem/svg-injector";
import React from "react";
import { parseInteractiveZonesSvg } from "@gettactic/helpers/src/map/svg/parseInteractiveZonesSvg";
import { buildInteractiveZonesFromMapData } from "@gettactic/map-builder/src/utils/buildInteractiveZonesFromMapData";
import type { MapCanvasValue } from "@gettactic/map-builder/src/components/map/contexts/MapCanvasContext/MapCanvasContext.types";
import { fillDynamicLabels } from "@/features/map/utils/utils-map";
import { useBooleanState } from "@gettactic/helpers/src";
import { useMap } from "@/features/map/MapProvider";
import { IResource } from "@gettactic/api";

export const useOnMapLoaded = (
  floor: IResource | null | undefined,
  onMapLoadedCallback?: (svg: SVGSVGElement | undefined) => void
) => {
  const { setResourcesIdFilter, setZonesInfo, schedulable, isMapBuilderMap } = useMap();
  const isLoading = useBooleanState();
  const svgRef = React.useRef<SVGSVGElement | null | undefined>(null);
  const timeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  const onMapLoaded: Errback = React.useCallback(
    (err, svg) => {
      if (err) {
        console.error(err);
        return;
      }
      isLoading.true();
      // save a SVG reference to reparse
      // during window resizes
      svgRef.current = svg;
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = null;

      const mapData = floor?.map_data
        ? (JSON.parse(floor.map_data) as MapCanvasValue)
        : null;

      // Judge by the floor actually passed in, not just the provider flag —
      // division (campus) maps come through as pseudo-floors that carry
      // map_data but aren't the provider's current floor.
      if (mapData && (isMapBuilderMap || !floor?.map_url)) {
        // Data-driven zones for map-builder maps — no DOM parsing needed
        const zones = buildInteractiveZonesFromMapData(mapData);
        setResourcesIdFilter(zones.zones.map((z) => z.id));
        setZonesInfo(zones);
        isLoading.false();
        onMapLoadedCallback?.(svg);
        return;
      }

      // Existing path for legacy SVG maps (DOM parsing)
      timeoutRef.current = setTimeout(() => {
        if (!svgRef.current) {
          return;
        }
        const zones = parseInteractiveZonesSvg(svgRef.current, {
          applyMatrixTransformToPosition: true
        });
        setResourcesIdFilter(zones?.zones.map((zone) => zone.id) || []);
        if (!zones) return;
        setZonesInfo(zones);
        isLoading.false();
      }, 100);
      fillDynamicLabels(svgRef.current as SVGElement, schedulable);
      onMapLoadedCallback?.(svg);
    },
    [svgRef, floor?.id, floor?.map_data, isMapBuilderMap]
  );

  const onMapUnloaded = () => {
    setZonesInfo({
      mapInfo: null,
      zones: []
    });
    setResourcesIdFilter([]);
    isLoading.false();
  };

  return { svgRef, onMapLoaded, onMapUnloaded, isLoading };
};
